/** Shared foreclosure case review status labels and helpers. */

export type ForeclosureCaseReviewStatus =
  | "needs_review"
  | "needs_registration"
  | "does_not_need_registration"
  | "exempt"
  | "unregistered_property_created"
  | "assigned";

export const FORECLOSURE_CASE_REVIEW_STATUS_OPTIONS: ForeclosureCaseReviewStatus[] =
  [
    "needs_review",
    "needs_registration",
    "assigned",
    "does_not_need_registration",
    "exempt",
    "unregistered_property_created",
  ];

export const FORECLOSURE_CASE_REVIEW_STATUS_LABELS: Record<
  ForeclosureCaseReviewStatus,
  string
> = {
  needs_review: "Needs Review",
  needs_registration: "Needs Registration",
  does_not_need_registration: "Does Not Need Registration",
  exempt: "Exempt",
  unregistered_property_created: "Unregistered Property Created",
  assigned: "Assigned",
};

export const FORECLOSURE_CASE_REVIEW_STATUS_UNREVIEWED_LABEL = "Unreviewed";

export type ForeclosureCaseReviewStatusBadgeVariant =
  | "default"
  | "secondary"
  | "outline"
  | "warning";

export function formatForeclosureCaseReviewStatus(
  status: ForeclosureCaseReviewStatus | null | undefined,
  options?: { empty?: string },
): string {
  if (status == null) {
    return options?.empty ?? FORECLOSURE_CASE_REVIEW_STATUS_UNREVIEWED_LABEL;
  }
  return FORECLOSURE_CASE_REVIEW_STATUS_LABELS[status] ?? status;
}

/** Append a new review note to any existing comment, separated by a newline. */
export function appendForeclosureCaseReviewComment(
  existing: string | null | undefined,
  addition: string | null | undefined,
): string | null {
  const current = existing?.trim() ?? "";
  const extra = addition?.trim() ?? "";
  if (!extra) {
    return current || null;
  }
  if (!current) {
    return extra;
  }
  return `${current}\n${extra}`;
}

export function buildBulkForeclosureCaseReviewUpdates(
  cases: Array<{
    county_fips: string;
    case_number: string;
    review_comment?: string | null;
  }>,
  options: {
    reviewStatus: ForeclosureCaseReviewStatus;
    commentToAppend: string;
  },
): Array<{
  countyFips: string;
  caseNumber: string;
  reviewStatus: ForeclosureCaseReviewStatus;
  reviewComment?: string | null;
}> {
  const extra = options.commentToAppend.trim();
  return cases.map((row) => ({
    countyFips: row.county_fips,
    caseNumber: row.case_number,
    reviewStatus: options.reviewStatus,
    ...(extra
      ? {
          reviewComment: appendForeclosureCaseReviewComment(
            row.review_comment,
            extra,
          ),
        }
      : {}),
  }));
}

export function foreclosureCaseReviewStatusBadgeVariant(
  status: ForeclosureCaseReviewStatus | null | undefined,
): ForeclosureCaseReviewStatusBadgeVariant {
  switch (status) {
    case "needs_review":
      return "outline";
    case "needs_registration":
      return "warning";
    case "assigned":
      return "default";
    case "unregistered_property_created":
      return "secondary";
    case "does_not_need_registration":
      return "secondary";
    case "exempt":
      return "secondary";
    default:
      return "outline";
  }
}
